import React from 'react';
import I18n from 'i18n-js';
import { flattenDeep } from 'lodash';
import { SearchResultFacet, SearchResultFacetGroup } from '@reverbdotcom/commons/src/gql/graphql';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCAccordion, RCCheckbox } from '@reverbdotcom/cadence/components';
import CollapsingList from '@reverbdotcom/commons/src/components/collapsing_list';
import CollapsingFacet from '@reverbdotcom/commons/src/components/collapsing_facet';
import classNames from 'classnames';

function hasSelectedChild(facet: SearchResultFacet) {
  if (!facet.subFacets) return false;
  return facet.subFacets?.some(sf => sf.selected);
}

export function isSomethingSelected(facet: SearchResultFacet, excludeAll = false) {
  return !(excludeAll && facet.all) && (facet.selected || hasSelectedChild(facet));
}

function getSelectedOptionsForFacet(facet: SearchResultFacet, acc = []) {
  if (facet.selected && !facet.all) {
    acc.push(facet);
  }
  if (facet?.subFacets) {
    facet.subFacets.forEach(subFacet => getSelectedOptionsForFacet(subFacet, acc));
  }
  return acc;
}

export function allSelectedFacets(facetGroups: SearchResultFacetGroup[]) {
  const allFacets = flattenDeep(facetGroups.map(fg => fg.facets || []));
  const allSelected = flattenDeep(allFacets.map(facet => getSelectedOptionsForFacet(facet, [])));
  return allSelected;
}

export const FACET_MAPPING = {
  'CHECKBOX': CheckboxSelectFacet,
  'NESTED_SELECT': NestedSelectFacet,
};

export const SIDEBAR_FACET_MAPPING = {
  'CHECKBOX': CheckboxSelectFacet,
  'NESTED_SELECT': SidebarNestedSelectFacet,
};

export function SidebarNestedSelectFacet({ facetGroup }: { facetGroup: SearchResultFacetGroup }) {
  return (
    <div className="facet facet--collapsible" data-testid={`nested-facet-group_${facetGroup.displayName}`}>
      <RCAccordion
        id={facetGroup.key}
        heading={facetGroup.displayName}
        defaultOpen={facetGroup.featured}
      >
        <ul className="facet__options">
          {
            facetGroup.facets.map((facet, idx) => {
              return <SelectFacetOption facet={facet} key={`${facet.displayName}-${idx}`} />;
            })
          }
        </ul>
      </RCAccordion>
    </div>
  );
}

export function NestedSelectFacet({ facetGroup }) {
  const allFacet = facetGroup?.facets.find(f => f.all);
  const somethingSelected = facetGroup?.facets.filter(f => isSomethingSelected(f, true));
  const baseFacets = somethingSelected.length > 0 ? somethingSelected : facetGroup.facets;
  const facets = allFacet && !baseFacets.includes(allFacet) ? [allFacet, ...baseFacets] : baseFacets;

  return (
    <div className="facet" data-testid={`nested-facet-group_${facetGroup.displayName}`}>
      <h3 className="facet__heading">{facetGroup.displayName}</h3>
      <ul className="facet__options">
        {
          facets.map((facet, idx) => {
            return <SelectFacetOption facet={facet} key={`${facet.displayName}-${idx}`} />;
          })
        }
      </ul>
    </div>
  );
}

function SelectFacetOption({ facet }) {
  const classes = classNames(`facet__option facet__option--${facet.key}`, {
    'facet__option--active ': facet.selected,
    'facet__option--child-active': hasSelectedChild(facet),
  });

  return (
    <div className={classes} >
      <CoreLink
        to={facet.searchUrl}
        className={facet.selected ? 'facet__link facet__link--active' : 'facet__link'}
        key={facet.displayName}
      >
        <div>
          <span className="mr-space">{facet.displayName}</span>
          <FacetCount facet={facet} />
        </div>
      </CoreLink>
      {isSomethingSelected(facet) && facet.subFacets &&
        <CollapsingFacet count={facet.subFacets.length} countThreshold={6}>
          <ul className="facet__options">
            {
              facet.subFacets.map(subFacet => {
                return <SelectFacetOption facet={subFacet} key={subFacet.displayName} />;
              })
            }
          </ul>
        </CollapsingFacet>
      }
    </div>
  );
}

interface CheckboxFilterProps {
  facetGroup: SearchResultFacetGroup;
  sidebar?: boolean;
}

export function CheckboxSelectFacet({ facetGroup, sidebar = false }: CheckboxFilterProps) {
  const classes = classNames('facet', { 'facet--collapsible': sidebar });
  return (
    <div className={classes} data-testid={`checkbox-facet-group_${facetGroup.displayName}`}>
      <RCAccordion
        id={facetGroup.key}
        heading={facetGroup.displayName}
        defaultOpen={sidebar ? (facetGroup.featured || facetGroup.facets.some(f => isSomethingSelected(f, true))) : true}
        zeroPadding={!sidebar}
        locked={!sidebar}
      >
        <CollapsingList renderInFilterList>
          {
            facetGroup.facets.map(facet => {
              return (
                <RCCheckbox
                  id={facet.displayName}
                  key={facet.displayName}
                  checked={facet.selected}
                  paddedHover
                  linkElement={(children: React.ReactNode) => (
                    <CoreLink to={facet.searchUrl} rel="nofollow" >
                      {children}
                    </CoreLink>
                  )}
                  label={
                    <div>
                      <span className="mr-space">{facet.displayName}</span>
                      <FacetCount facet={facet} />
                    </div>
                  }
                />
              );
            })
          }
        </CollapsingList>
      </RCAccordion>
    </div>
  );
}

function FacetCount({ facet }: { facet: SearchResultFacet }) {
  if (!facet.count) { return null; }

  return (
    <span className="color-secondary weight-normal">
      ({I18n.toNumber(facet.count, { precision: 0 })})
    </span>
  );
}
